import { Button } from "@/components/ui/Button";
import PageHeader from "@/custolink-views/asset_frontend/ui/layout/PageHeader";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PublicSupportVideoCallsApi } from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import Loading from "@/components/shared/Loading";
import PageContent from "@/custolink-views/asset_frontend/ui/layout/PageContent";
import { VideoRoom, VideoRoomEndedInfo } from "@/custolink-views/shared/VideoRoomPage";
import { MobileTechnicianRoom } from "@/custolink-newdesign/pages/support/room/MobileTechnicianRoom";
import { useAssetFrontendDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";
import VideoByLinkDesign from "@/custolink-views/asset_frontend/video_by_link/VideoByLinkDesign";

/**
 * The page an invite link opens on. The room is rendered inline and full
 * screen rather than in a popup: mobile browsers either block window.open or
 * turn it into a detached tab, which left the recipient staring at a page that
 * said the call had opened somewhere else.
 */
export default function VideoByLink() {
  const { roomId } = useParams<{ roomId: string }>();
  // Read unconditionally: the branches below return early (into the in-call room, or the
  // classic markup), and a hook cannot be called only on some renders.
  const newDesign = useAssetFrontendDesign();
  const [joined, setJoined] = useState(false);
  // The session ended for good: an org user closed it, or it timed out. Nothing to come back to.
  const [finished, setFinished] = useState(false);
  // The customer left the room on their own. The session may still be open, so this offers a
  // way back in rather than a dead end.
  const [left, setLeft] = useState(false);

  const result = useQuery({
    queryFn: () =>
      PublicSupportVideoCallsApi.enduserJoinInstantvideolink(roomId as string),
    queryKey: ["VideoByLink"],
    enabled: roomId != null && roomId.trim().length > 0,
  });

  const statusCheck = useMutation({
    mutationFn: () =>
      PublicSupportVideoCallsApi.enduserInstantVideolinkStatus(roomId as string),
    onSuccess: (r) => {
      if (r.closed) {
        setFinished(true);
        setJoined(false);
        setLeft(false);
      }
    },
  });

  // Poll the backend while in the call or while the customer has stepped away, to notice
  // when the support worker ends the session or the empty-session timer closes it. The
  // LiveKit connection alone does not tell us while joined: the room stays up when the
  // other participant simply leaves. While left, a rejoin button must never outlive its
  // session, so this is what keeps it honest.
  useEffect(() => {
    if ((!joined && !left) || finished || roomId == null) return;
    const interval = setInterval(() => {
      statusCheck.mutate();
    }, 1500);
    return () => clearInterval(interval);
  }, [joined, left, finished, roomId]);

  const handleJoin = () => {
    setJoined(true);
    setLeft(false);
  };

  // Re-runs the join query so a stale token is never handed back into the room: the session
  // may have moved on (e.g. a new worker joined) in the time the customer was away from it.
  const handleRejoin = () => {
    result.refetch().then((r) => {
      if (r.isSuccess) {
        setJoined(true);
        setLeft(false);
        return;
      }
      // enduserJoinInstantvideolink throws for a terminal session - the most likely reason a
      // rejoin fails is that the session ended in the moment between our last poll and this
      // click. Show the same "over" screen rather than leaving a rejoin button that no
      // longer works.
      setFinished(true);
      setLeft(false);
    });
  };

  const handleEnded = useCallback((info: VideoRoomEndedInfo) => {
    setJoined(false);
    if (info.remoteClose) {
      // An org user ended the session, or it timed out. Nothing to come back to.
      setFinished(true);
      return;
    }
    // They left on their own. The session is still open for the next ten minutes, so offer
    // the way back in rather than a dead end.
    setLeft(true);
  }, []);

  // The customer is joining, so the in-call room follows the asset-frontend switch like
  // everything else on this page, not the admin switch - see rule in the newdesign HANDOFF.
  if (joined && result.data != null) {
    return newDesign ? (
      <MobileTechnicianRoom
        token={result.data.livekitToken}
        serverUrl={result.data.livekitServerUrl}
        recording={result.data.recordingEnabled}
        title={result.data.organisationName}
        subtitle={"Support: " + result.data.callerDisplayName}
        roomId={roomId}
        onEnded={handleEnded}
      />
    ) : (
      <VideoRoom
        token={result.data.livekitToken}
        serverUrl={result.data.livekitServerUrl}
        role="customer"
        roomId={roomId}
        recording={result.data.recordingEnabled}
        onEnded={handleEnded}
      />
    );
  }

  if (newDesign) {
    return (
      <VideoByLinkDesign
        isPending={result.isPending}
        isError={result.isError || result.error != null}
        data={result.data}
        finished={finished}
        left={left}
        onJoin={handleJoin}
        onRejoin={handleRejoin}
      />
    );
  }

  return (
    <div className={"flex flex-col"} style={{ height: "100dvh" }}>
      <PageHeader>
        <div className="flex items-center justify-between">
          <h3 className={"text-white"}>Video Call</h3>
          <div></div>
        </div>
      </PageHeader>

      <PageContent cover={true}>
        {result.isPending && (
          <div className="relative m-4">
            <Loading loading={true} />
          </div>
        )}

        {(result.isError || result.error) && (
          <div className="relative m-4">
            <p>
              Error. The video could not be opened. The video chat may have been
              closed.
            </p>
          </div>
        )}

        {result.data != null && finished && (
          <div className="relative m-4 text-center space-y-4">
            <h3>Video call has been closed</h3>
            <p className="mt-2 text-base">The video call has ended.</p>
          </div>
        )}

        {result.data != null && !finished && left && (
          <div className="flex flex-col items-center justify-center gap-6 h-full px-4 text-center">
            <div className="space-y-2">
              <h3>You have left the call</h3>
              <p className="text-base">
                The call is still open. You can rejoin if you need to.
              </p>
            </div>
            <Button
              variant={"solid"}
              size={"lg"}
              className="w-full max-w-xs"
              onClick={handleRejoin}
            >
              Rejoin
            </Button>
          </div>
        )}

        {result.data != null && !finished && !left && (
          <div className="flex flex-col items-center justify-center gap-6 h-full px-4 text-center">
            <div className="space-y-2">
              <h3>{result.data.organisationName}</h3>
              <p className="text-base">
                {result.data.callerDisplayName} is waiting for you on a video call, or will
                join shortly.
              </p>
              <p className="text-sm opacity-70">
                Your camera and microphone are used for this call.
              </p>
            </div>
            <Button
              variant={"solid"}
              size={"lg"}
              className="w-full max-w-xs"
              onClick={handleJoin}
            >
              Join video call
            </Button>
          </div>
        )}
      </PageContent>
    </div>
  );
}
